/**
 * ClaritySkillEffect — Conjuration Tether path upgrade: stronger shade,
 * adjustable mirror distance.
 *
 * Clarity is an upgrade node enhancing Tether. Two mechanical effects:
 *
 * 1. **Shade damage multiplier** rises from Tether's 0.40 baseline to 0.55.
 *    This is registered as a `shadeDamageMultiplier` resolver key with `max`
 *    aggregation — TetherSkillEffect reads it via `getMaxEffect('tether',
 *    'shadeDamageMultiplier')`. The resolver registration happens in the
 *    integration layer from JSON data; this SkillEffect does not interact
 *    with the resolver for the multiplier.
 *
 * 2. **Mirror distance cycling.** The player can toggle the shade's mirror
 *    distance between 2, 3, and 4 tiles. This SkillEffect owns the cycling
 *    state: a `currentDistanceIndex` that advances through the options array
 *    on each `cycleMirrorDistance()` call, wrapping at the end. The default
 *    index is 1 (3 tiles — identical to base Tether).
 *
 * Integration hook:
 *   - `getCurrentMirrorDistanceTiles()` — TetherDeps.getMirrorDistanceTiles
 *     is wired to this method. TetherSkillEffect reads it at spawn time to
 *     position the shade.
 *   - `cycleMirrorDistance()` — bound to a cycle-button input in the
 *     integration layer.
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see conjuration-spec.md § Clarity (tether_clarity)
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Skill tuning
// ---------------------------------------------------------------------------

export interface ClarityConfig {
  /** Ordered distance options in tiles, cycled by player input. */
  readonly mirrorDistanceOptions: readonly number[];
  /** Starting index into mirrorDistanceOptions. Default 1 → 3 tiles. */
  readonly defaultDistanceIndex: number;
}

const DEFAULT_CONFIG: ClarityConfig = {
  mirrorDistanceOptions: [2, 3, 4],
  defaultDistanceIndex: 1,  // index 1 → 3 tiles (same as base Tether)
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class ClaritySkillEffect implements SkillEffect {
  readonly skillId = 'tether_clarity';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly config: ClarityConfig;
  private _currentDistanceIndex: number;
  private _cycleCount = 0;

  constructor(config: Partial<ClarityConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
    this._currentDistanceIndex = this.config.defaultDistanceIndex;
  }

  // --- Clarity-specific API ---

  /**
   * Current mirror distance in tiles. TetherSkillEffect reads this at shade
   * spawn time via TetherDeps.getMirrorDistanceTiles.
   */
  getCurrentMirrorDistanceTiles(): number {
    return this.config.mirrorDistanceOptions[this._currentDistanceIndex];
  }

  /**
   * Advance to the next mirror distance option, wrapping around at the end.
   * Bound to a cycle-button input by the integration layer.
   */
  cycleMirrorDistance(): void {
    this._currentDistanceIndex =
      (this._currentDistanceIndex + 1) % this.config.mirrorDistanceOptions.length;
    this._cycleCount++;
  }

  // --- State getters (observable by scenarios and integration layer) ---

  /** Current index into the distance options array. */
  get currentDistanceIndex(): number { return this._currentDistanceIndex; }
  /** Number of times cycleMirrorDistance() has been called. */
  get cycleCount(): number { return this._cycleCount; }
  /** The full set of distance options this node provides. */
  get distanceOptions(): readonly number[] { return this.config.mirrorDistanceOptions; }

  // --- SkillEffect interface ---

  canActivate(): boolean {
    return false; // upgrade — not manually activated
  }

  activate(): boolean {
    return false;
  }

  isActive(): boolean {
    return false;
  }

  get cooldown(): number { return 0; }

  update(_delta: number): void {
    // Upgrade — distance cycling is event-driven, not tick-driven
  }

  destroy(): void {
    // No resources to release
  }
}
